import { Box, createStyles, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { ListBox, Text } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { useCallback, useMemo, useState } from 'react';
import type { ISecodaEntity, SearchResult } from '../../api';
import {
	useDeleteResourceRelation,
	useResourceRelationList,
} from '../../api/hooks/relations';
import { getEntityDisplayTitle } from '../../lib/utils/entity';
import { useCheckEntityUpdatePermission } from '../../utils/authorization/roles';
import { buildResourceUrl } from '../../utils/navigationUtils';
import { RelatedResourceCreateMenu } from '../EntityPageLayout/SidesheetStacks/RelatedResource/RelatedResourceCreateMenu';
import RelatedResourceItem from '../EntityPageLayout/SidesheetStacks/RelatedResource/RelatedResourceItem';
import { ResourceLinkType } from '../Link/useResourceLink';
import { ResourcePreview } from '../ResourcePreview';
import { InlineRelatedResourcesBody } from './InlineRelatedResourcesBody';
import { InlineRelatedResourcesError } from './InlineRelatedResourcesError';
import { InlineRelatedResourcesLoading } from './InlineRelatedResourcesLoading';
import { RelatedResourcePill } from './RelatedResourcePill';

const useStyles = createStyles((theme) => ({
	wrapper: {
		flex: '1 !important',
		minHeight: space[7],
		maxWidth: '100%',
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing.xs,
		overflow: 'hidden',
		alignItems: 'center',
	},
	overflowButton: {
		background: theme.other.getColor('fill/transparent-secondary/default'),
		borderRadius: theme.radius.sm,
		height: space[5],
		border: 'none',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		padding: `0 ${theme.spacing['2xs']}`,
	},
}));

export interface InlineRelatedResourcesProps {
	entity: ISecodaEntity;
}

// Helper function to convert ISecodaEntity to SearchResult for RelatedResourceItem
const convertEntityToSearchResult = (entity: ISecodaEntity): SearchResult => ({
	id: entity.id,
	title: entity.title || 'Untitled',
	entity_type: entity.entity_type,
	native_type: entity.native_type,
	icon: entity.icon,
	description: entity.description,
	definition: entity.definition,
	search_metadata: entity.search_metadata,
	workspace: entity.workspace_id,
	pii: entity.pii,
	verified: entity.verified,
	integration: entity.integration,
	parent: entity.parent || undefined,
	internal_usage: entity.internal_usage,
	external_usage: entity.external_usage,
	internal_users: entity.internal_users,
	external_users: entity.external_users,
	collections: entity.collections,
	sources: entity.sources,
	tags: entity.tags,
	dqs: entity.dqs,
	display_metadata: entity.display_metadata,
	created_at: entity.created_at,
	updated_at: entity.updated_at,
});

// I tried to do this dynamically based on the height of the container, but it was too complicated to implement.
// see https://github.com/secoda/secoda/blob/43835bd4f3682d1da0f843d66971b23fb5b37b7e/apps/frontend/src/hooks/useOverflowCalculation.ts
const MAX_VISIBLE_ITEMS = 7;

export function InlineRelatedResources({
	entity,
}: InlineRelatedResourcesProps) {
	const { classes } = useStyles();
	const [popoverOpened, setPopoverOpened] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const editorOfEntity = useCheckEntityUpdatePermission({
		entity,
	});

	const { data, isLoading, isError } = useResourceRelationList({
		entityId: entity.id,
	});

	const { mutateAsync: deleteRelation } = useDeleteResourceRelation({
		onSuccess: () => {
			showNotification({
				message: 'Resource relation removed',
				color: 'green',
			});
		},
		onError: () => {
			showNotification({
				message: 'Error deleting resource relation',
				color: 'red',
			});
		},
	});

	const navigate = useNavigate();

	const relatedEntityIds = useMemo(
		() => (data?.results ?? []).map((r) => r.id),
		[data]
	);

	const { visibleResources, hiddenResources } = useMemo(() => {
		const resources = data?.results ?? [];

		if (resources.length <= MAX_VISIBLE_ITEMS) {
			return {
				visibleResources: resources,
				hiddenResources: [],
			};
		}

		return {
			visibleResources: resources.slice(0, MAX_VISIBLE_ITEMS),
			hiddenResources: resources.slice(MAX_VISIBLE_ITEMS),
		};
	}, [data]);

	// Filter and sort hidden resources based on search term
	const filteredHiddenResources = useMemo(() => {
		let resources = hiddenResources;

		// Filter by search term if provided
		if (searchTerm.trim()) {
			resources = resources.filter((resource) => {
				const displayTitle = getEntityDisplayTitle(resource);
				return displayTitle.toLowerCase().includes(searchTerm.toLowerCase());
			});
		}

		// Sort alphabetically by display title
		return resources.sort((a, b) => {
			const titleA = getEntityDisplayTitle(a).toLowerCase();
			const titleB = getEntityDisplayTitle(b).toLowerCase();
			return titleA.localeCompare(titleB);
		});
	}, [hiddenResources, searchTerm]);

	const handleOpenChange = useCallback(
		(newOpened: boolean) => {
			setSearchTerm('');
			setPopoverOpened(newOpened);
		},
		[setSearchTerm]
	);

	const handleOnRemove = useCallback(
		(id: string) =>
			async function RemoveRelatedResource() {
				await deleteRelation({
					fromEntityId: entity.id,
					toEntityId: id,
				});
			},
		[deleteRelation, entity.id]
	);

	if (isLoading) {
		return <InlineRelatedResourcesLoading />;
	}

	if (isError) {
		return <InlineRelatedResourcesError />;
	}

	return (
		<InlineRelatedResourcesBody>
			<Group align="flex-start" className={classes.wrapper}>
				{visibleResources.map((resource) => (
					<RelatedResourcePill
						key={resource.id}
						entity={resource}
						onRemove={editorOfEntity ? handleOnRemove(resource.id) : undefined}
					/>
				))}

				{hiddenResources.length > 0 && (
					<ListBox
						opened={popoverOpened}
						onOpenChange={handleOpenChange}
						closeOnEscape
					>
						<ListBox.Target>
							<Box
								className={classes.overflowButton}
								onClick={() => setPopoverOpened(!popoverOpened)}
							>
								<Text color="text/secondary/default" size="sm" py="4xs">
									+{hiddenResources.length} more
								</Text>
							</Box>
						</ListBox.Target>

						<ListBox.ItemsDropdown
							usePortal
							items={filteredHiddenResources.map(convertEntityToSearchResult)}
							renderItem={(searchResult, getProps) => (
								<ResourcePreview
									id={searchResult.id}
									data={
										{
											...searchResult,
											archived: false,
											stale: false,
										} as any
									}
									resourceType={ResourceLinkType.Entity}
									isLoading={false}
									hasError={false}
									openDelay={200}
									closeDelay={50}
									withinPortal
									position="right-start"
									disabled={false}
								>
									<div>
										<RelatedResourceItem
											key={searchResult.id}
											item={searchResult}
											isViewerUser={!editorOfEntity}
											onClick={(item) => {
												navigate(
													buildResourceUrl({
														id: item.id,
														entity_type: item.entity_type,
													})
												);
											}}
											onRemove={
												editorOfEntity
													? handleOnRemove(searchResult.id)
													: undefined
											}
											getProps={getProps}
										/>
									</div>
								</ResourcePreview>
							)}
						/>
					</ListBox>
				)}
				{editorOfEntity && (
					<RelatedResourceCreateMenu
						entity={entity}
						relatedEntityIds={relatedEntityIds}
						size="sm"
					/>
				)}
			</Group>
		</InlineRelatedResourcesBody>
	);
}
